import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Button, 
  Table, 
  Select, 
  Space, 
  Card, 
  Typography, 
  Row, 
  Col, 
  Avatar, 
  Tag, 
  message,
  Input,
  Badge,
  List,
  Empty,
  Spin
} from 'antd';
import { 
  DownloadOutlined, 
  UserOutlined,
  PhoneOutlined,
  ContactsOutlined,
  TeamOutlined,
  SearchOutlined
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const { Text, Title } = Typography;

const ScheduleModal = ({ isVisible, onClose, popupData = [], classChangeLogs = [] }) => {
  const { t } = useTranslation();
  
  // Student selection states - UPDATED FOR GROUPS
  const [showStudentSelection, setShowStudentSelection] = useState(false);
  const [studentGroups, setStudentGroups] = useState([]); // Changed from students to studentGroups
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentGroup, setSelectedStudentGroup] = useState(null); // Changed to group
  const [registrationDate, setRegistrationDate] = useState(new Date().toISOString().split('T')[0]); // Default to today in YYYY-MM-DD format
  // Contact selection states
  const [showContactSelection, setShowContactSelection] = useState(false);
  const [contacts, setContacts] = useState([
  { id: 1, name: 'TFU Main Office', phone: '02-574-2671' },
  { id: 2, name: 'Tiger', phone: '092-418-2772' },
  { id: 3, name: 'Baifern', phone: '095-163-2827' },
]);

  const [loadingContacts, setLoadingContacts] = useState(false);
  
const [selectedContact, setSelectedContact] = useState(contacts[0]);

  const [customContact, setCustomContact] = useState({
    name: '',
    phone: ''
  });
  const [isCustomContact, setIsCustomContact] = useState(false);
  
  const API_URL = process.env.REACT_APP_API_URL;

  // Predefined contacts
  const defaultContacts = [
  { id: 1, name: 'TFU Main Office', phone: '02-574-2671' },
  { id: 2, name: 'Tiger', phone: '092-418-2772' },
  { id: 3, name: 'Baifern', phone: '095-163-2827' },
];

  // Fetch student groups function - UPDATED
  const fetchStudentGroups = useCallback(async (search = '') => {
    try {
      setLoadingStudents(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(`${API_URL}/students/schedule-info-add`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { search }
      });

      if (response.data.success) {
        // Handle both old and new API response formats
        if (response.data.studentGroups) {
          // New grouped format
          setStudentGroups(response.data.studentGroups);
        } else if (response.data.students) {
          // Old format - convert to groups (fallback)
          const students = response.data.students;
          const groups = students.map(student => ({
            type: 'group',
            value: student.value,
            label: student.label,
            displayLabel: student.label,
            status: student.status,
            totalStudents: 1,
            mainStudent: {
              userid: student.value,
              firstname: student.label.split(' ')[0] || '',
              lastname: student.label.split(' ').slice(1).join(' ') || '',
              nickname: '',
              email: student.contactInfo?.email || '',
              phone: student.contactInfo?.phone || '',
              address: student.contactInfo?.address || '',
              age: student.age,
              currenteducationlevel: student.currenteducationlevel,
              schoolname: student.schoolname,
              gender: student.gender,
              type: 'main'
            },
            linkedStudents: [],
            allStudents: [{
              userid: student.value,
              firstname: student.label.split(' ')[0] || '',
              lastname: student.label.split(' ').slice(1).join(' ') || '',
              nickname: '',
              email: student.contactInfo?.email || '',
              phone: student.contactInfo?.phone || '',
              address: student.contactInfo?.address || '',
              age: student.age,
              currenteducationlevel: student.currenteducationlevel,
              schoolname: student.schoolname,
              gender: student.gender,
              type: 'main'
            }]
          }));
          setStudentGroups(groups);
        }
      } else {
        console.warn('[fetchStudentGroups] Response returned success: false');
      }
    } catch (error) {
      console.error('[fetchStudentGroups] Error:', error);
      message.error('Failed to fetch student groups');
    } finally {
      setLoadingStudents(false);
    }
  }, [API_URL]);

  // Fetch contacts function
  const fetchContacts = useCallback(async (search = '') => {
    try {
      setLoadingContacts(true);
      
      let filteredContacts = defaultContacts;
      
      if (search) {
        filteredContacts = defaultContacts.filter(contact => 
          contact.name.toLowerCase().includes(search.toLowerCase()) ||
          contact.phone.includes(search)
        );
      }
      
      setContacts(filteredContacts);
    } catch (error) {
      console.error('[fetchContacts] Error:', error);
      message.error('Failed to fetch contacts');
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // Load student groups on mount
  useEffect(() => {
    if (showStudentSelection) {
      fetchStudentGroups();
    }
  }, [showStudentSelection, fetchStudentGroups]);

  // Load contacts on mount
  useEffect(() => {
    if (showContactSelection) {
      fetchContacts();
    }
  }, [showContactSelection, fetchContacts]);

useEffect(() => {
  if (!isVisible) {
    // Reset all modal-related state when closed
    setSelectedStudentGroup(null);
    setShowStudentSelection(false);
    setShowContactSelection(false);
    setSelectedContact({ id: 1, name: 'TFU Main Office', phone: '02-574-2671' }); // Add id here
    setIsCustomContact(false);
    setCustomContact({ name: '', phone: '' });
    setSearchTerm('');
    setStudentGroups([]);
    setContacts([
      { id: 1, name: 'TFU Main Office', phone: '02-574-2671' },
      { id: 2, name: 'Tiger', phone: '092-418-2772' },
      { id: 3, name: 'Baifern', phone: '095-163-2827' },
    ]); // Update this too
    setRegistrationDate(new Date().toISOString().split('T')[0]);
  }
}, [isVisible]);

useEffect(() => {
  if (Array.isArray(popupData) && popupData.length > 0 && popupData[0].salesperson_nickname) {
    setSelectedContact({
      id: 'sales',
      name: popupData[0].salesperson_nickname,
      phone: popupData[0].salesperson_phone || '-',
      email: popupData[0].salesperson_email || '-'
    });
  }
}, [popupData]);



// Helper function to format date as dd/mm/yy
const formatDateToDDMMYY = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date)) return '';
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  
  return `${day}/${month}/${year}`; // Using forward slashes like your example
};

  // Handle student group search - UPDATED
  const handleStudentSearch = (value) => {
    setSearchTerm(value);
    fetchStudentGroups(value);
  };

  // Handle group selection - UPDATED
  const handleGroupSelect = (group) => {
    setSelectedStudentGroup(group);
    message.success(`Selected: ${group.mainStudent.firstname} ${group.mainStudent.lastname} (${group.totalStudents} students)`);
  };

  // Handle contact search
  const handleContactSearch = (value) => {
    fetchContacts(value);
  };

  // Handle contact selection
  const handleContactSelect = (contactId) => {
    if (contactId === 'custom') {
      setIsCustomContact(true);
      setSelectedContact({ name: '', phone: '' });
    } else {
      const contact = contacts.find(c => c.id === contactId);
      if (contact) {
        setIsCustomContact(false);
        setSelectedContact(contact);
      }
    }
  };

  // Handle custom contact input
  const handleCustomContactChange = (field, value) => {
    setCustomContact(prev => ({
      ...prev,
      [field]: value
    }));
    setSelectedContact(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Render student group item - NEW
  const renderStudentGroupItem = (group) => {
    const isSelected = selectedStudentGroup?.value === group.value;
    
    return (
      <List.Item
        onClick={() => handleGroupSelect(group)}
        style={{
          cursor: 'pointer',
          backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
          border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
          borderRadius: '8px',
          margin: '8px 0',
          padding: '12px',
          transition: 'all 0.3s ease',
        }}
      >
        <List.Item.Meta
          avatar={
            <Badge count={group.linkedStudents.length} showZero={false}>
              <Avatar 
                size={48}
                icon={group.linkedStudents.length > 0 ? <TeamOutlined /> : <UserOutlined />}
                style={{
                  backgroundColor: group.status === 'active' ? '#52c41a' : '#faad14'
                }}
              />
            </Badge>
          }
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold' }}>
                {group.mainStudent.nickname || `${group.mainStudent.firstname} ${group.mainStudent.lastname}`}
              </span>
              <Tag color={group.status === 'active' ? 'green' : 'orange'}>
                {group.status}
              </Tag>
              {group.linkedStudents.length > 0 && (
                <Tag color="blue">
                  +{group.linkedStudents.length} linked
                </Tag>
              )}
            </div>
          }
          description={
            <div>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>Main:</strong> {group.mainStudent.firstname} {group.mainStudent.lastname}
                {group.mainStudent.age && ` (Age: ${group.mainStudent.age})`}
              </div>
              {group.linkedStudents.length > 0 && (
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  <strong>Linked:</strong> {group.linkedStudents.map(ls => 
                    `${ls.firstname} ${ls.lastname}`
                  ).join(', ')}
                </div>
              )}
              {group.mainStudent.schoolname && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                  <strong>School:</strong> {group.mainStudent.schoolname}
                </div>
              )}
            </div>
          }
        />
        <div style={{ textAlign: 'right', color: '#1890ff', fontWeight: 'bold' }}>
          {group.totalStudents} student{group.totalStudents > 1 ? 's' : ''}
        </div>
      </List.Item>
    );
  };

  // Helper function to get change reason display
  const getChangeReasonDisplay = (reason) => {
    const reasonMap = {
      'admin_adjust': { color: 'blue', text: 'Admin' },
      'cancelled_by_student': { color: 'red', text: 'CL by S.' },
      'cancelled_by_teacher': { color: 'orange', text: 'CL by T.' },
      'class_on_hold_by_student': { color: 'geekblue', text: 'On hold by S.' },
      'class_on_hold_by_school': { color: 'green', text: 'On hold by P.' },
      'cancel_same_day': { color: 'volcano', text: 'Cancel Same Day' },
      'make_up': { color: 'purple', text: 'Make Up' },
      'others': { color: 'default', text: 'Others' }
    };
    
    return reasonMap[reason] || { text: reason || '-', color: 'black' };
  };
  
  // Sort the data by date first, then by time
  const sortedData = [...(Array.isArray(popupData) ? popupData : [])].sort((a, b) => {
    const dateTimeA = new Date(`${a.date}T${a.schedule_time || '00:00'}`);
    const dateTimeB = new Date(`${b.date}T${b.schedule_time || '00:00'}`);
    return dateTimeA - dateTimeB;
  });
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
  
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    }).format(date);
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

const formatMinutesToHours = (minutes) => {
  if (!minutes || minutes === 0) return '0.00 hr';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}.${remainingMinutes.toString().padStart(2, '0')} hr`;
};

  // Function to load Sarabun font into jsPDF
  const loadSarabunFont = (doc) => {
    doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
    doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
    doc.setFont('Sarabun');
  };


// UPDATED PDF generation function
const handleDownloadPDF = () => {
  const doc = new jsPDF();
  loadSarabunFont(doc);

  const margin = 15;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;

  const headerImage = new Image();
  headerImage.src = '/images/tfulogo.png';

  headerImage.onload = () => {
    // Smaller logo
    const imgAspectRatio = headerImage.width / headerImage.height;
    const targetHeight = 25; 
    const targetWidth = targetHeight * imgAspectRatio;

    // Simple header without box
    const logoX = centerX - (targetWidth / 2); // Center the logo
    const logoY = margin;
    doc.addImage(headerImage, 'PNG', logoX, logoY, targetWidth, targetHeight);

    // School name and title below logo
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(25, 42, 86);
    doc.text('Schedule', doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);


    let currentY = margin + 40;

    // Get class details
    const classCode = popupData[0]?.class_code || 'N/A';
    const isPrivate = popupData[0]?.isprivate;
    const type = isPrivate === true ? 'Private' : 'Group';
    const totalHours = popupData?.[0]?.total_hours || 0;

    const getPlatformText = (platform) => {
      switch(platform) {
        case 'online': return 'Online';
        case 'onsite': return 'Onsite';
        case 'athome': return 'At Home';
        default: return platform || 'N/A';
      }
    };
    
    const platform = getPlatformText(popupData?.[0]?.platform);

      const sortedDates = popupData
    .map(item => new Date(item.date))
    .sort((a, b) => a - b);
  const startDate = formatDate(sortedDates[0]);
  const endDate = formatDate(sortedDates[sortedDates.length - 1]);

    if (selectedStudentGroup) {
      // Calculate total height needed for all students
      const studentBoxHeight = 25; // Much smaller height per student box


      // Check if we need to handle page overflow
      let studentsProcessed = 0;
      let remainingStudents = [...selectedStudentGroup.allStudents];

      while (remainingStudents.length > 0) {
        // Calculate how many students can fit on current page
        const remainingHeight = pageHeight - currentY - 80; // Reserve space for footer
        const studentsPerPage = Math.floor(remainingHeight / studentBoxHeight);
        const studentsThisPage = remainingStudents.splice(0, Math.max(1, studentsPerPage));

        studentsThisPage.forEach((student, index) => {
          // Check if we need a new page
          if (currentY + studentBoxHeight > pageHeight - 80) {
            doc.addPage();
            currentY = margin + 20;
          }

          // Draw very thin student box border
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2); // Very thin border
          doc.rect(margin, currentY, pageWidth - (margin * 2), studentBoxHeight);

          // Student info with tight spacing and small fonts
          doc.setFontSize(10); // Smaller font
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);

          const studentName = `${student.firstname} ${student.lastname}`;
          const nickname = student.nickname ? ` (${student.nickname})` : '';
          const fullName = studentName + nickname;
          
          // Very tight padding from borders
          const leftPadding = margin + 2;
          const topPadding = currentY + 8;
          
          doc.text(`Student's Name:`, leftPadding, topPadding);
          doc.setFont('helvetica', 'normal');
          doc.text(fullName, leftPadding + 30, topPadding);

          // Age - positioned after name with minimal spacing
          doc.setFont('helvetica', 'bold');
          doc.text(`Age:`, centerX + 23, topPadding);
          doc.setFont('helvetica', 'normal');
          doc.text(String(student.age || ''), centerX + 33, topPadding);

          // Gender checkboxes - compact and right-aligned
          const genderText = `( ${student.gender === 'male' ? 'X' : ' '} ) Male ( ${student.gender === 'female' ? 'X' : ' '} ) Female`;
          doc.text(genderText, centerX + 43, topPadding);

          // Second row - School, Level, Course Type with minimal spacing
          const secondRowY = topPadding + 10;
          
          doc.setFont('helvetica', 'bold');
          doc.text(`School:`, leftPadding, secondRowY);
          doc.setFont('helvetica', 'normal');
          doc.text(student.schoolname || 'N/A', leftPadding + 15, secondRowY);

          doc.setFont('helvetica', 'bold');
          doc.text(`Level:`, centerX - 10, secondRowY);
          doc.setFont('helvetica', 'normal');
          doc.text(student.currenteducationlevel || 'N/A', centerX + 2, secondRowY);

          doc.setFont('helvetica', 'bold');
          doc.text(`Venue:`, centerX + 18, secondRowY);
          doc.setFont('helvetica', 'normal');
          doc.text(`${platform}`, centerX + 33, secondRowY);

          doc.setFont('helvetica', 'bold');
          doc.text(`Type:`, centerX + 50, secondRowY);
          doc.setFont('helvetica', 'normal');
          doc.text(type, centerX + 65, secondRowY);

          currentY += studentBoxHeight + 3; // Minimal spacing between boxes
        });

        // If there are more students, add a new page
        if (remainingStudents.length > 0) {
          doc.addPage();
          currentY = margin + 20;
        }


          // Get subject-teacher pairings from the data
          const subjectTeacherPairs = popupData
            .filter(slot => slot.subject_name && slot.nickname) // Only include rows with both subject and teacher
            .reduce((pairs, slot) => {
              const key = `${slot.subject_name}|${slot.nickname}`;
              if (!pairs.some(pair => `${pair.subject}|${pair.teacher}` === key)) {
                pairs.push({
                  subject: slot.subject_name,
                  teacher: slot.nickname
                });
              }
              return pairs;
            }, []);

          // Calculate box height based on content
          const maxWidth = pageWidth - (margin * 2) - 4;
          const leftColumnWidth = (maxWidth * 0.6) - 35; // 60% for subjects (minus label space)
          const rightColumnWidth = (maxWidth * 0.4) - 20; // 40% for teachers

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          // Calculate total lines needed (add extra space for course header)
          let totalLines = Math.max(1, subjectTeacherPairs.length);
          const lineHeight = 4;
          const boxHeight = 16 + (totalLines * lineHeight) + 8; // Extra space for course header

          // Check if we need space for subjects/teachers box
          if (currentY + boxHeight > pageHeight - 80) {
            doc.addPage();
            currentY = margin + 20;
          }

          // Draw subjects and teachers box
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(margin, currentY, pageWidth - (margin * 2), boxHeight);

          const leftPadding = margin + 2;
          const rightColumnStart = margin + (maxWidth * 0.6);
          let textY = currentY + 8;

          // Course header at the top
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(25, 42, 86); // Dark blue color
          doc.text(`Course: ${classCode}`, leftPadding, textY);

          textY += 8; // Space after course header

          // Add column headers
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);

          doc.text(`Subject(s):`, leftPadding, textY);
          doc.text(`Teacher(s):`, rightColumnStart, textY);

          // Add subject-teacher pairs
          doc.setFont('helvetica', 'normal');
          textY += 6; // Move down from headers

          subjectTeacherPairs.forEach((pair, index) => {
            const yPos = textY + (index * lineHeight);
            
            // Subject on left (with bullet point)
            const subjectText = `• ${pair.subject}`;
            const subjectLines = doc.splitTextToSize(subjectText, leftColumnWidth);
            doc.text(subjectLines[0], leftPadding + 5, yPos); // Only show first line to keep alignment
            
            // Teacher on right
            doc.text(pair.teacher, rightColumnStart + 5, yPos);
          });

          currentY += boxHeight + 5; // Move past the subjects/teachers box // Move past the subjects/teachers box
                          }

                // Add class summary info after all student boxes
                currentY += 2;

              // First row: Total Hours and Date of Registration
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);

          doc.text(`Registration Date:`, margin, currentY);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(25, 42, 86);
          const manualRegDate = registrationDate ? formatDate(registrationDate) : startDate;
          doc.text(manualRegDate, margin + 40, currentY);

          // Total Hours (right aligned)
          doc.setFont('helvetica', 'bold');
          doc.text(`Total Hours:`, pageWidth - margin - 50, currentY, { align: 'right' });
          doc.setFontSize(12);
          doc.setTextColor(25, 42, 86);
          doc.text(formatMinutesToHours(totalHours), pageWidth - margin - 15, currentY, { align: 'right' });

          currentY += 8; // More space between rows

          // Second row: Contact info - properly spaced to fit in one row
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);

          doc.text(`Contact Person:`, margin, currentY);
          doc.setFont('helvetica', 'normal');
          doc.text(selectedContact.name || 'N/A', margin + 40, currentY);

          // Phone
          doc.setFont('helvetica', 'bold');
          doc.text(`Phone:`, margin + 108, currentY);
          doc.setFont('helvetica', 'normal');
          doc.text(selectedContact.phone || '-', margin + 150, currentY);

                currentY += 10;

              } else {
                // No student selected - show placeholder
                doc.setFontSize(12);
                doc.setTextColor(128, 128, 128);
                doc.text('No student selected', centerX, currentY, { align: 'center' });
                currentY += 10;
              }


          const tableStartY = currentY;

          const columns = [
            { header: 'No.', dataKey: 'index' },
            { header: t('day'), dataKey: 'schedule_day' },
            { header: t('date'), dataKey: 'date' },
            { header: t('time'), dataKey: 'time' },
            { 
              header: t('hours'), 
              dataKey: 'schedule_hour',
              render: (hourString) => {
                // Ensure hourString is a string
                const safeHourString = hourString ? String(hourString) : '';

                if (!safeHourString) return '0.00 hr';

                // Parse formats like "2h 0m" or "2h 45m"
                const match = safeHourString.match(/(\d+)h\s*(\d+)m/);

                if (match) {
                  const hours = match[1];
                  const minutes = match[2].padStart(2, '0');
                  return `${hours}.${minutes} hr`;
                }

                // Fallback
                return safeHourString;
              }
            },
              { header: t('teacher'), dataKey: 'nickname' },
            { header: t('subject'), dataKey: 'subject_name' },
            { header: t('classchange'), dataKey: 'reason' }
          ];

          doc.autoTable({
            startY: tableStartY,
            head: [columns.map(col => col.header)],
            body: sortedData.map((row, index) =>
              [
                index + 1, 
                (() => {
            const day = row.schedule_day;
            if (!day) return '';
            
            const dayMap = {
              'Monday': 'Mon',
              'Tuesday': 'Tue', 
              'Wednesday': 'Wed',
              'Thursday': 'Thu',
              'Friday': 'Fri',
              'Saturday': 'Sat',
              'Sunday': 'Sun'
            };
            
            return dayMap[day] || day.substring(0, 3);
          })(),
                formatDate(row.date),
                
                `${formatTime(row.schedule_time)} - ${formatTime(row.end_time)}`,
                (() => {
                  let hourString = row.schedule_hour;

                  // Convert all types to safe string
                  const safe = hourString ? String(hourString) : '';

                  if (!safe) return '0.00 hr';

                  // Matches "2h 30m"
                  const match = safe.match(/(\d+)h\s*(\d+)m/);

                  if (match) {
                    const hours = match[1];
                    const minutes = match[2].padStart(2, '0');
                    return `${hours}.${minutes} hr`;
                  }

                  // If backend sends raw number of minutes (ex: 120)
                  if (!isNaN(Number(safe))) {
                    const minutes = Number(safe);
                    const h = Math.floor(minutes / 60);
                    const m = String(minutes % 60).padStart(2, '0');
                    return `${h}.${m} hr`;
                  }

                  // Fallback
                  return safe;
                })(),

                row.nickname,
                row.subject_name,
                (() => {
                  const changeLog = classChangeLogs.find(ccl => ccl.classid === row.classid);
                  const reasonDisplay = changeLog
                    ? getChangeReasonDisplay(changeLog.changereason)
                    : { text: '-' };
                  return reasonDisplay.text;
                })()
              ]
            ),
            theme: 'grid',
            styles: {
              font: 'Sarabun',
              fontStyle: 'normal',
              fontSize: 9,
              textColor: [51, 51, 51],
              lineColor: [200, 200, 200],
            },
            headStyles: {
              fillColor: [25, 42, 86],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 10
            },
            alternateRowStyles: {
              fillColor: [252, 252, 252]
            },
            margin: { 
              top: 10,    // Reduced from 20 to move table up
              bottom: 20, // Increased from 60 to prevent footer overlap
              left: margin,
              right: margin
            },
            pageBreak: 'auto',
            showHead: 'everyPage', // Ensure header shows on every page
            tableLineColor: [200, 200, 200],
            tableLineWidth: 0.1
          });

              // Add agreement section - ensure proper spacing from table
              const pageCount = doc.internal.getNumberOfPages();
              doc.setPage(pageCount);
              
              const finalY = doc.lastAutoTable.finalY || tableStartY + 100;
              let agreementStartY = finalY + 15;
              // Check if we need a new page for agreement
              if (agreementStartY > pageHeight - 100) {
                doc.addPage();
                agreementStartY = margin + 20;
              }
              
              // Agreement header
              doc.setFontSize(10);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(51, 51, 51);
              doc.text('Agreement:', margin, agreementStartY);
              
              // Agreement text
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(51, 51, 51);
              
              const agreementText = [
                'Class cancellation should be made 48 hours before class meeting, or the unexpected cancelled',
                'hours will be deducted for a course teacher. Schedule change can be made no more than 3 times.',
                'Student and/or parent or guardian shall be responsible for any damage or injury which he/she causes to',
                'persons or TFU property. I, therefore, agree to these terms and agreements.',
                'Registered lesson hours are valid for 2 years (24 months) and will automatically expire upon the end of this period.'
              ];
              
              let currentAgreementY = agreementStartY + 6;
              agreementText.forEach((line, index) => {
                doc.text(line, margin, currentAgreementY + (index * 4));
              });
              
              // Signature lines - ensure they don't overlap with footer
              const signatureY = Math.min(currentAgreementY + (agreementText.length * 4) + 15, pageHeight - 40);
              const signatureLineLength = 60;
              
              // Student signature
              doc.text('Student', margin, signatureY);
              doc.line(margin, signatureY + 2, margin + signatureLineLength, signatureY + 2);
              
              // Course Teacher signature (center)
              const centerSignatureX = centerX - 30;
              doc.text('Course Teacher', centerSignatureX, signatureY);
              doc.line(centerSignatureX, signatureY + 2, centerSignatureX + signatureLineLength, signatureY + 2);
              
              // Head Teacher signature (right)
              const rightSignatureX = pageWidth - margin - signatureLineLength;
              doc.text('Head Teacher', rightSignatureX, signatureY);
              doc.line(rightSignatureX, signatureY + 2, rightSignatureX + signatureLineLength, signatureY + 2);

              // Footer with page numbers and printed date - ensure no overlap
              const newPageCount = doc.internal.getNumberOfPages();
              for(let i = 1; i <= newPageCount; i++) {
                doc.setPage(i);
                
                // Page numbers
                doc.setTextColor(128, 128, 128);
                doc.setFontSize(8);
                doc.text(
                  `Page ${i} of ${newPageCount}`, 
                  centerX, 
                  pageHeight - 15,
                  { align: 'center' }
                );
                
                // Printed date footer
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(128, 128, 128);
                const footerY = pageHeight - 8;
                const currentDate = new Date().toLocaleDateString('en-GB');
                doc.text(`Printed on ${currentDate}`, centerX, footerY, { align: 'center' });
              }

              // Generate filename
              let fileName;
              if (selectedStudentGroup) {
                const mainStudentName = `${selectedStudentGroup.mainStudent.firstname}_${selectedStudentGroup.mainStudent.lastname}`;
                if (selectedStudentGroup.linkedStudents.length > 0) {
                  fileName = `${classCode}_${mainStudentName}_group_${selectedStudentGroup.totalStudents}students.pdf`;
                } else {
                  fileName = `${classCode}_${mainStudentName}.pdf`;
                }
              } else {
                fileName = `${classCode}.pdf`;
              }
              
              doc.save(fileName);
              message.success('PDF downloaded successfully!');
            };

            headerImage.onerror = () => {
              console.error('Failed to load the header image.');
              message.error('Failed to load header image');
            };
          };

  const columns = [
    {
    title: 'No.',
    key: 'index',
    render: (text, record, index) => index + 1
  },
    { title: t('day'), dataIndex: 'schedule_day', key: 'schedule_day' },
    { title: t('date'), dataIndex: 'date', key: 'date', render: formatDate },

    {
      title: t('time'),
      key: 'time',
      render: (text, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`
    },
    { title: t('hours'), dataIndex: 'schedule_hour', key: 'schedule_hour' },
    { title: t('teacherName'), dataIndex: 'nickname', key: 'nickname' },
    { title: t('subjectName'), dataIndex: 'subject_name', key: 'subject_name' },
    { 
      title: 'Platform', 
      dataIndex: 'platform', 
      key: 'platform',
      render: (platform) => {
        switch(platform) {
          case 'online':
            return 'Online';
          case 'onsite':
            return 'Onsite';
          case 'athome':
            return 'At Home';
          default:
            return platform || 'N/A';
        }
      }
    },
    { 
      title: t('classchange'), 
      key: 'reason',
      render: (text, record) => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
        const reasonDisplay = changeLog
          ? getChangeReasonDisplay(changeLog.changereason) 
          : { text: '-', color: 'black' };
        return (
          <span style={{ color: reasonDisplay.color }}>
            {reasonDisplay.text}
          </span>
        );
      }
    },
    { 
      title: t('note'), 
      key: 'note',
      render: (text, record) => {
        const changeLog = classChangeLogs.find(ccl => ccl.classid === record.classid);
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {changeLog?.note || '-'}
          </div>
        );
      }
    }
  ];

  // Determine which view to show
  const getCurrentView = () => {
    if (showContactSelection) return 'contact';
    if (showStudentSelection) return 'student';
    return 'main';
  };

  const currentView = getCurrentView();

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{sortedData?.[0]?.class_code || t('scheduleDetails')}</span>
          {selectedStudentGroup && (
            <Tag color="blue">
              {selectedStudentGroup.mainStudent.firstname} {selectedStudentGroup.mainStudent.lastname}
              {selectedStudentGroup.linkedStudents.length > 0 && 
                ` (+${selectedStudentGroup.linkedStudents.length} linked)`
              }
            </Tag>
          )}
          {selectedContact && (
            <Tag color="green" icon={<PhoneOutlined />}>
              {selectedContact.name}: {selectedContact.phone}
            </Tag>
          )}
        </div>
      }
      width={currentView === 'main' ? 1200 : 1000}
    >
      {currentView === 'main' && (
        <>
          {/* UPDATED: Selected Student Group Info */}
          {selectedStudentGroup && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f8ff' }}>
              <Row align="middle">
                <Col flex="auto">
                  <Space>
                    <Badge count={selectedStudentGroup.linkedStudents.length} showZero={false}>
                      <Avatar 
                        icon={selectedStudentGroup.linkedStudents.length > 0 ? <TeamOutlined /> : <UserOutlined />} 
                        style={{ backgroundColor: '#1890ff' }}
                      >
                        {selectedStudentGroup.mainStudent.firstname?.[0]?.toUpperCase()}
                      </Avatar>
                    </Badge>
                    <div>
                      <Text strong>
                        {selectedStudentGroup.mainStudent.firstname} {selectedStudentGroup.mainStudent.lastname}
                        {selectedStudentGroup.linkedStudents.length > 0 && 
                          ` (+${selectedStudentGroup.linkedStudents.length} linked students)`
                        }
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Total: {selectedStudentGroup.totalStudents} students • 
                        {selectedStudentGroup.mainStudent.email} • {selectedStudentGroup.mainStudent.phone}
                      </Text>
                      {selectedStudentGroup.linkedStudents.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Linked: {selectedStudentGroup.linkedStudents.map(ls => 
                              `${ls.firstname} ${ls.lastname}`
                            ).join(', ')}
                          </Text>
                        </div>
                      )}
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Button 
                    size="small" 
                    onClick={() => setSelectedStudentGroup(null)}
                  >
                    Change Group
                  </Button>
                </Col>
              </Row>
            </Card>
          )}

          {/* Selected Contact Info */}
          {selectedContact && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
              <Row align="middle">
                <Col flex="auto">
                  <Space>
                    <Avatar icon={<ContactsOutlined />} style={{ backgroundColor: '#52c41a' }}>
                      {selectedContact.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <div>
                      <Text strong>Contact: {selectedContact.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <PhoneOutlined /> {selectedContact.phone}
                      </Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Button 
                    size="small" 
                    onClick={() => setShowContactSelection(true)}
                  >
                    Change Contact
                  </Button>
                </Col>
              </Row>
            </Card>
          )}
{/* Add Registration Date input after contact info */}
{selectedContact && (
  <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fff7e6' }}>
    <Row align="middle">
      <Col flex="auto">
        <Space>
          <Text strong>Registration Date:</Text>
          
          {/* Hidden native date picker */}
          <input
            type="date"
            id="hidden-registration-date"
            value={registrationDate}
            onChange={(e) => setRegistrationDate(e.target.value)}
            required
            style={{ display: 'none' }}
          />

          {/* Visible formatted input */}
          <input
            type="text"
            readOnly
            value={formatDateToDDMMYY(registrationDate)}
            placeholder="dd-mm-yy"
            onClick={() =>
              document
                .getElementById('hidden-registration-date')
                ?.showPicker()
            }
            style={{
              padding: '4px 8px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px',
              width: '80px',
              height: '32px',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          />
        </Space>
      </Col>
    </Row>
  </Card>
)}
          {/* UPDATED: Action Buttons */}
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button 
                type="primary"
                icon={<UserOutlined />}
                onClick={() => setShowStudentSelection(true)}
              >
                {selectedStudentGroup ? 'Change Student Group' : 'Select Student Group'}
              </Button>
              
              <Button 
                icon={<ContactsOutlined />}
                onClick={() => setShowContactSelection(true)}
              >
                {selectedContact ? 'Change Contact' : 'Select Contact'}
              </Button>
              
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleDownloadPDF}
                disabled={!selectedStudentGroup}
              >
                {t('downloadPDF')}
              </Button>
            </Space>
          </div>

          {/* Schedule Table */}
          <Table
            dataSource={sortedData}
            columns={columns}
            rowKey={(record) => record.id}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            style={{
              overflowX: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          />
        </>
      )}

      {currentView === 'student' && (
        <>
          {/* UPDATED: Student Group Selection View */}
          <div style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0 }}>Select Student Group</Title>
              </Col>
              <Col>
                <Button onClick={() => setShowStudentSelection(false)}>
                  Back to Schedule
                </Button>
              </Col>
            </Row>
          </div>

         <Card>
  <div style={{ marginBottom: 16 }}>
    <Text strong>Search and Select Student Group:</Text>
  </div>
  
  <Input
    placeholder="Type to search by name, nickname, or linked students..."
    prefix={<SearchOutlined />}
    value={searchTerm}
    onChange={(e) => handleStudentSearch(e.target.value)}
    style={{ marginBottom: '16px' }}
    size="large"
  />

  {loadingStudents ? (
    <div style={{ textAlign: 'center', padding: '40px' }}>
      <Spin size="large" />
    </div>
  ) : searchTerm.trim() === '' ? (
    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
      <SearchOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
      <div>Start typing to search for students...</div>
      <div style={{ fontSize: '12px', marginTop: '4px' }}>
        Search by name, nickname, or linked student names
      </div>
    </div>
  ) : studentGroups.length === 0 ? (
    <Empty 
      description={`No student groups found for "${searchTerm}"`}
      style={{ padding: '40px' }}
    />
  ) : (
    <>
      <div style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>
        Found {studentGroups.length} student group{studentGroups.length !== 1 ? 's' : ''}
      </div>
      <List
        dataSource={studentGroups}
        renderItem={renderStudentGroupItem}
        style={{ maxHeight: '400px', overflowY: 'auto' }}
      />
    </>
  )}

  {selectedStudentGroup && (
    <Card 
      size="small" 
      style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', marginTop: 16 }}
    >
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ color: '#52c41a' }}>Selected Group:</Text>
      </div>
      
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Main Student:</Text>
            <br />
            <Text>{selectedStudentGroup.mainStudent.firstname} {selectedStudentGroup.mainStudent.lastname}</Text>
            {selectedStudentGroup.mainStudent.nickname && (
              <Text type="secondary"> ({selectedStudentGroup.mainStudent.nickname})</Text>
            )}
          </div>
          
          <div style={{ marginBottom: 8 }}>
            <Text strong>Email:</Text>
            <br />
            <Text>{selectedStudentGroup.mainStudent.email || 'N/A'}</Text>
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text strong>Age:</Text>
            <br />
            <Text>
              {selectedStudentGroup.mainStudent.age !== null && selectedStudentGroup.mainStudent.age !== undefined
                ? selectedStudentGroup.mainStudent.age
                : 'N/A'}
            </Text>
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text strong>School Name:</Text>
            <br />
            <Text>{selectedStudentGroup.mainStudent.schoolname || 'N/A'}</Text>
          </div>
        </Col>
        <Col span={12}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Phone:</Text>
            <br />
            <Text>{selectedStudentGroup.mainStudent.phone || 'N/A'}</Text>
          </div>
          
          <div style={{ marginBottom: 8 }}>
            <Text strong>Gender:</Text>
            <br />
            <Text>{selectedStudentGroup.mainStudent.gender || 'N/A'}</Text>
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text strong>Education Level:</Text>
            <br />
            <Text>{selectedStudentGroup.mainStudent.currenteducationlevel || 'N/A'}</Text>
          </div>
          
          <div style={{ marginBottom: 8 }}>
            <Text strong>Total Students:</Text>
            <br />
            <Tag color="blue">{selectedStudentGroup.totalStudents}</Tag>
          </div>
        </Col>
      </Row>

      {selectedStudentGroup.linkedStudents.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ color: '#1890ff' }}>Linked Students ({selectedStudentGroup.linkedStudents.length}):</Text>
          <div style={{ marginTop: 8 }}>
            {selectedStudentGroup.linkedStudents.map((student, index) => (
              <Card key={student.id} size="small" style={{ marginBottom: 8, backgroundColor: '#f0f8ff' }}>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Text strong>{index + 1}. {student.firstname} {student.lastname}</Text>
                    {student.nickname && <Text type="secondary"> ({student.nickname})</Text>}
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Age: {student.age || 'N/A'} | Gender: {student.gender || 'N/A'}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      School: {student.schoolname || 'N/A'}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Education: {student.currenteducationlevel || 'N/A'}
                    </Text>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Button onClick={() => setSelectedStudentGroup(null)}>
            Clear Selection
          </Button>
          <Button 
            type="primary" 
            onClick={() => setShowStudentSelection(false)}
          >
            Confirm Selection
          </Button>
        </Space>
      </div>
    </Card>
  )}
</Card>
        </>
      )}

      {currentView === 'contact' && (
        <>
          {/* Contact Selection View - UNCHANGED */}
          <div style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0 }}>Select Contact Person</Title>
              </Col>
              <Col>
                <Button onClick={() => setShowContactSelection(false)}>
                  Back to Schedule
                </Button>
              </Col>
            </Row>
          </div>

          <Card>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Choose Contact Person:</Text>
            </div>

                <Select
                  showSearch
                  placeholder="Select a contact person or choose custom..."
                  style={{ width: '100%', marginBottom: 16 }}
                  loading={loadingContacts}
                  onSearch={handleContactSearch}
                  onChange={handleContactSelect}
                  value={isCustomContact ? 'custom' : selectedContact?.id}
                  filterOption={false}
                  notFoundContent={loadingContacts ? 'Loading...' : 'No contacts found'}
                >
                  {contacts.map(contact => (
                    <Select.Option key={contact.id} value={contact.id}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>
                          <UserOutlined /> {contact.name} <PhoneOutlined /> {contact.phone}
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                  <Select.Option key="custom" value="custom">
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        + Custom Contact
                      </div>
                    </div>
                  </Select.Option>
                </Select>

            {isCustomContact && (
              <Card 
                size="small" 
                style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591', marginBottom: 16 }}
              >
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ color: '#fa8c16' }}>Custom Contact:</Text>
                </div>
                
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Contact Name:</Text>
                      <Input
                        placeholder="Enter contact person name"
                        value={customContact.name}
                        onChange={(e) => handleCustomContactChange('name', e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Phone Number:</Text>
                      <Input
                        placeholder="Enter phone number"
                        value={customContact.phone}
                        onChange={(e) => handleCustomContactChange('phone', e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            )}

            {selectedContact && selectedContact.name && selectedContact.phone && (
              <Card 
                size="small" 
                style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
              >
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ color: '#52c41a' }}>Selected Contact:</Text>
                </div>
                
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Contact Person:</Text>
                      <br />
                      <Text>{selectedContact.name}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Phone Number:</Text>
                      <br />
                      <Text><PhoneOutlined /> {selectedContact.phone}</Text>
                    </div>
                  </Col>
                </Row>
                
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => {
                      setSelectedContact({ id: 1, name: 'TFU Main Office', phone: '02-574-2671' }); // Add id here
                      setIsCustomContact(false);
                      setCustomContact({ name: '', phone: '' });
                    }}>
                      Reset to Default
                    </Button>
                    <Button 
                      type="primary" 
                      onClick={() => setShowContactSelection(false)}
                      disabled={!selectedContact.name || !selectedContact.phone}
                    >
                      Confirm Selection
                    </Button>
                  </Space>
                </div>
              </Card>
            )}
          </Card>
        </>
      )}
    </Modal>
  );
};


export default ScheduleModal;